import { useEffect, useRef, useState } from 'react';

/**
 * Indian-PIN-code → city / district / state / country auto-fill.
 *
 * Hits the free `api.postalpincode.in` lookup. Returns the FIRST
 * post-office under that PIN (most reliable city/state pair).
 * Debounces by 400 ms so a user typing the 6 digits doesn't fire
 * five wasted requests.
 *
 * Usage:
 *   const { status, city, district, state, country } = usePincodeLookup(form.postalCode);
 *   useEffect(() => {
 *     if (city)    set('city', city);
 *     if (state)   set('state', state);
 *     if (country) set('country', country);
 *   }, [city, state, country]);
 *
 * Errors are surfaced via `status === 'error'` — fall back to manual
 * entry. The hook never throws.
 */

export interface PincodeLookupResult {
  status: 'idle' | 'loading' | 'ok' | 'error' | 'invalid';
  city: string | null;
  district: string | null;
  state: string | null;
  country: string | null;
  error: string | null;
}

const PINCODE_RE = /^[1-9][0-9]{5}$/;
const cache = new Map<string, PincodeLookupResult>();

export function usePincodeLookup(pincode: string | null | undefined, debounceMs = 400): PincodeLookupResult {
  const [result, setResult] = useState<PincodeLookupResult>({
    status: 'idle',
    city: null,
    district: null,
    state: null,
    country: null,
    error: null,
  });
  // Hold the active AbortController so a fast typer cancels the
  // in-flight request before it returns.
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const pin = (pincode ?? '').trim();
    if (!pin) {
      setResult({ status: 'idle', city: null, district: null, state: null, country: null, error: null });
      return;
    }
    if (!PINCODE_RE.test(pin)) {
      setResult({ status: 'invalid', city: null, district: null, state: null, country: null, error: 'Invalid PIN format' });
      return;
    }
    // Cache hit?
    const cached = cache.get(pin);
    if (cached) {
      setResult(cached);
      return;
    }

    let cancelled = false;
    const t = window.setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      setResult({ status: 'loading', city: null, district: null, state: null, country: null, error: null });

      // PRIMARY: KSS backend's own pincode endpoint (G58, shipped
      // 2026-05-29). Proxies upstream providers + caches the result,
      // so we don't depend on the public APIs' TLS state and we get a
      // single-request answer. Returns:
      //   200 { pincode, city, district, state, country, source }
      //   502 { error: "upstream_unavailable", attempted: [...] }
      const tryBackend = async (): Promise<PincodeLookupResult | null> => {
        try {
          const r = await fetch(`/api/master-data/pincode/${pin}`, { signal: ctrl.signal });
          if (!r.ok) return null;
          const json = (await r.json()) as {
            city?: string; district?: string; state?: string; country?: string;
          };
          if (!json.city && !json.state) return null;
          return {
            status: 'ok',
            city:    json.city    ?? null,
            district: json.district ?? null,
            state:   json.state   ?? null,
            country: json.country === 'IN' ? 'India' : (json.country ?? 'India'),
            error: null,
          };
        } catch {
          return null;
        }
      };

      // Fallback 1: api.postalpincode.in
      //   ⚠️ As of 2026-05-21 this host's TLS cert expired (and at the
      //   time of writing the maintainer hadn't rotated it yet). The
      //   browser blocks the fetch and the user sees no auto-fill.
      //   We try anyway in case it gets fixed, then fall through to a
      //   secondary provider with a valid cert.
      const tryPostalPincodeIn = async (): Promise<PincodeLookupResult | null> => {
        try {
          const r = await fetch(`https://api.postalpincode.in/pincode/${pin}`, { signal: ctrl.signal });
          if (!r.ok) return null;
          const json = (await r.json()) as Array<{
            Status?: string;
            PostOffice?: Array<{ Name?: string; District?: string; State?: string; Country?: string }>;
          }>;
          const entry = Array.isArray(json) ? json[0] : null;
          if (!entry || entry.Status !== 'Success' || !Array.isArray(entry.PostOffice) || entry.PostOffice.length === 0) {
            return null;
          }
          const po = entry.PostOffice[0]!;
          return {
            status: 'ok',
            city: po.Name ?? null,
            district: po.District ?? null,
            state: po.State ?? null,
            country: po.Country ?? 'India',
            error: null,
          };
        } catch {
          return null;
        }
      };

      // Fallback: api.worldpostallocations.com (valid cert, broad coverage).
      const tryWorldPostal = async (): Promise<PincodeLookupResult | null> => {
        try {
          const r = await fetch(
            `https://api.worldpostallocations.com/?postalcode=${pin}&countrycode=IN`,
            { signal: ctrl.signal },
          );
          if (!r.ok) return null;
          const json = (await r.json()) as {
            status?: boolean;
            result?: Array<{ postalLocation?: string; state?: string; district?: string; country?: string }>;
          };
          if (json.status !== true || !json.result?.length) return null;
          const row = json.result[0]!;
          return {
            status: 'ok',
            city: row.postalLocation ?? row.district ?? null,
            district: row.district ?? null,
            state: row.state ?? null,
            country: row.country === 'IN' ? 'India' : (row.country ?? 'India'),
            error: null,
          };
        } catch {
          return null;
        }
      };

      try {
        // Backend first (fastest + cached), then the two public providers.
        const out =
          (await tryBackend()) ??
          (await tryPostalPincodeIn()) ??
          (await tryWorldPostal());
        if (cancelled) return;
        if (out) {
          cache.set(pin, out);
          setResult(out);
        } else {
          const err: PincodeLookupResult = {
            status: 'error', city: null, district: null, state: null, country: null,
            error: 'PIN not found in any provider',
          };
          cache.set(pin, err);
          setResult(err);
        }
      } catch (e) {
        if (cancelled) return;
        if ((e as { name?: string })?.name === 'AbortError') return;
        setResult({
          status: 'error', city: null, district: null, state: null, country: null,
          error: (e as Error)?.message ?? 'Lookup failed',
        });
      }
    }, debounceMs);

    return () => {
      cancelled = true;
      window.clearTimeout(t);
      abortRef.current?.abort();
    };
  }, [pincode, debounceMs]);

  return result;
}
